import React from 'react';
import Skeleton from '../../../styled components/Skeleton';
import {
  BookingCell,
  HappeningNowCellContent,
  HappeningNowDotPlaceholder
} from './ListViewTableRow/ListViewTableRow.style';
import type { ListViewColumn } from '../../types';
import type { SkeletonProps, SkeletonWidth } from '../../../styled components/Skeleton';

/* About as many rows as a screenful, so the table reads as a filled table
   while it waits rather than as an empty one */
const placeholderRows = [0, 1, 2, 3, 4, 5];

/* How long a formatted value of a column type is, in characters: what
   formatCellValue makes of it, at the length the format settings give it. A
   column of a known type gets exactly that width, so the start and end columns
   come out as wide as each other — the way the times themselves will. */
const charWidthOfType: Record<string, number> = {
  time: 5 /* 00:00 */,
  date: 10 /* 31/12/2026 */,
  datetime: 16 /* 31/12/2026 00:00 */,
  number: 4
};

/* Text has no length to go on, so it gets a share of its column, cycled per
   cell to read as values of differing lengths instead of a grid of identical
   bars */
const textCellWidths: SkeletonWidth[] = ['1/2', '3/4', '1/3', 'full', '2/3', '1/4'];

/* Enough of a floor that a share of a narrow column still reads as a value
   rather than as a dash */
const textCellMinWidth = 60;

const cellSkeletonProps = (column: ListViewColumn, cellIndex: number): SkeletonProps => {
  const charWidth = column.type === undefined ? undefined : charWidthOfType[column.type];
  if (charWidth !== undefined) return { $charWidth: charWidth };
  return {
    $width: textCellWidths[cellIndex % textCellWidths.length],
    $minWidth: textCellMinWidth
  };
};

interface ListViewTableLoadingRowsProps {
  columns: ListViewColumn[];
  /* The column the "happening now" dot is put in front of: its cells hold the
     dot's room open here too, so the loading table is laid out exactly the way
     the bookings will land in it */
  happeningNowColumnKey: string;
}

/* The table's rows while its bookings are being fetched. The columns are known
 * without them, so the table is already the one the bookings will land in —
 * only its values are skeletons. */
const ListViewTableLoadingRows = ({
  columns,
  happeningNowColumnKey
}: ListViewTableLoadingRowsProps) => (
  <>
    {placeholderRows.map((rowIndex) => (
      <tr key={rowIndex} data-test-list-view-table-loading-row>
        {columns.map((column, columnIndex) => {
          const skeleton = (
            <Skeleton {...cellSkeletonProps(column, rowIndex + columnIndex)} />
          );
          return (
            <BookingCell key={column.key} $changed={false}>
              {column.key === happeningNowColumnKey ? (
                <HappeningNowCellContent>
                  <HappeningNowDotPlaceholder />
                  {skeleton}
                </HappeningNowCellContent>
              ) : (
                skeleton
              )}
            </BookingCell>
          );
        })}
      </tr>
    ))}
  </>
);

export default ListViewTableLoadingRows;
